import {
  type V2AdminCreditDetailDto,
  type V2AdminCreditsListDto,
  type V2AdminCreditsSummaryDto,
  type V2AdvanceCreditStageResultDto,
  type V2StartCreditDto,
  v2AdminCreditsControllerAdvance,
  v2AdminCreditsControllerAnswer,
  v2AdminCreditsControllerCheck,
  v2AdminCreditsControllerFinancingType,
  v2AdminCreditsControllerFindOne,
  v2AdminCreditsControllerList,
  v2AdminCreditsControllerStart,
  v2AdminCreditsControllerSummary
} from '@idc-forms/api-client'
import {
  V2AdminCreditDetailSchema,
  V2AdminCreditsListSchema,
  V2AdminCreditsSummarySchema,
  type V2AdvanceCreditStageInput,
  V2AnswerCreditFieldInputSchema,
  type V2CheckCreditItemInput,
  type V2SetCreditFinancingTypeInput
} from '@idc-forms/schemas/api-v2/admin-credits'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'
import type { CreditSort } from '../models/credit-list'

export type CreditsAdminV2Query = {
  filters?: Record<string, unknown>
  sort?: CreditSort
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

export type CreditAdvanceWarnings = V2AdvanceCreditStageResultDto['warnings']
type CreditDetailField = V2AdminCreditDetailDto['stages'][number]['items'][number]['fields'][number]
export type CreditAnswerInput = { value: CreditDetailField['value'] }

function toListQuery(query: CreditsAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length > 0 ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const creditsAdminV2Service = {
  list: async (accessToken: string, query: CreditsAdminV2Query): Promise<V2AdminCreditsListDto> => {
    const result = await v2AdminCreditsControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminCreditsListSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<CreditsAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminCreditsSummaryDto> => {
    const result = await v2AdminCreditsControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminCreditsSummarySchema, result.data)
  },

  /**
   * Abrir el expediente a mano — el camino manual de R10.
   *
   * Se direcciona por `neg_` y no por `crd_` porque el expediente todavía NO existe: la fila del
   * listado ES la negociación cerrada, y ese es el único id que hay para nombrarla. La respuesta es
   * el expediente completo, el mismo presentador que el `GET`, así que sirve para sembrar la caché
   * del detalle sin un segundo viaje.
   *
   * ⚠️ Toma el `V2StartCreditDto` GENERADO y no el `V2StartCreditInput` de Zod: aquél trae el
   * `neg_` *branded*, y la web no maneja ids branded en ningún lado —el wire los entrega como
   * `string` pelado—. Pedirlo acá obligaría a un cast en cada llamada, que es peor que tipar el
   * contrato que realmente viaja.
   */
  start: async (accessToken: string, input: V2StartCreditDto): Promise<V2AdminCreditDetailDto> => {
    const result = await v2AdminCreditsControllerStart({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body: input
    })
    // ☠️ SIN `assertShape`, igual que las otras cinco escrituras — y por la razón que
    // `assert-shape.ts` declara: **sólo se validan las LECTURAS**. Un alta que contestó 201 YA
    // OCURRIÓ; hacerla fallar porque el cuerpo de vuelta no cuadra le miente a la persona sobre
    // algo que sí se guardó.
    //
    // No es teórico: la primera versión de este método SÍ validaba, y el resultado medido fue el
    // peor posible — el expediente quedaba creado en la base y la pantalla no hacía absolutamente
    // nada. Ni panel, ni toast, ni fila actualizada. Invitaba a apretar de nuevo y comerse un
    // `CREDIT_ALREADY_EXISTS` sobre el expediente que uno mismo acababa de abrir.
    return result.data
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminCreditDetailDto> => {
    const result = await v2AdminCreditsControllerFindOne({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminCreditDetailSchema, result.data)
  },

  checkItem: async (
    accessToken: string,
    id: string,
    stageKey: string,
    itemKey: string,
    input: V2CheckCreditItemInput
  ): Promise<V2AdminCreditDetailDto> => {
    const result = await v2AdminCreditsControllerCheck({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id, stageKey, itemKey },
      body: input
    })
    return result.data
  },

  answerField: async (
    accessToken: string,
    id: string,
    stageKey: string,
    itemKey: string,
    fieldKey: string,
    input: CreditAnswerInput
  ): Promise<V2AdminCreditDetailDto> => {
    const result = await v2AdminCreditsControllerAnswer({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id, stageKey, itemKey, fieldKey },
      body: V2AnswerCreditFieldInputSchema.parse(input)
    })
    return result.data
  },

  advanceStage: async (
    accessToken: string,
    id: string,
    stageKey: string,
    input: V2AdvanceCreditStageInput
  ): Promise<V2AdvanceCreditStageResultDto> => {
    const result = await v2AdminCreditsControllerAdvance({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id, stageKey },
      body: input
    })
    return result.data
  },

  setFinancingType: async (
    accessToken: string,
    id: string,
    input: V2SetCreditFinancingTypeInput
  ): Promise<V2AdminCreditDetailDto> => {
    const result = await v2AdminCreditsControllerFinancingType({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: input
    })
    return result.data
  }
}
